import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { briefingService, Briefing } from '../services/briefingService';
import { BriefingForm } from '../components/BriefingForm';

export function PublicBriefing() {
  const { token } = useParams<{ token: string }>();
  const [briefing, setBriefing] = useState<Briefing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await briefingService.getByToken(token!);
        setBriefing(data);
      } catch (e: any) {
        setError(e.response?.data?.error || 'Link de briefing inválido ou expirado');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleSubmit = async (dados: Record<string, any>) => {
    setSubmitting(true);
    try {
      await briefingService.responderPublico(token!, dados);
      setDone(true);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erro ao enviar o briefing');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async (dados: Record<string, any>) => {
    setSaving(true);
    try {
      const b = await briefingService.salvarPublico(token!, dados);
      setBriefing(b);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erro ao salvar o rascunho');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-card/50 border border-border/50 rounded-xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Não foi possível carregar</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-card/50 border border-border/50 rounded-xl p-8 text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Briefing enviado!</h1>
          <p className="text-muted-foreground">
            Obrigado por preencher o briefing. Nossa equipe comercial analisará as informações e entrará em contato em breve.
          </p>
        </div>
      </div>
    );
  }

  if (briefing && briefing.status !== 'PENDENTE') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-card/50 border border-border/50 rounded-xl p-8 text-center">
          <CheckCircle className="w-12 h-12 text-blue-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Briefing já respondido</h1>
          <p className="text-muted-foreground">
            Este briefing já foi respondido. Se precisar, entre em contato com nossa equipe.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-foreground">Briefing de Novo Projeto</h1>
          <p className="text-muted-foreground">
            Preencha o formulário abaixo com o máximo de detalhes para agilizar a análise da nossa equipe.
          </p>
          {briefing?.cliente && (
            <p className="text-sm text-muted-foreground">
              Empresa: {briefing.cliente.nomeFantasia || briefing.cliente.razaoSocial}
            </p>
          )}
        </div>

        <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-8">
          {briefing?.mensagemRevisao && (
            <div className="mb-6 bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-1">
                Solicitação de revisão da nossa equipe
              </h3>
              <p className="text-sm text-foreground whitespace-pre-wrap">{briefing.mensagemRevisao}</p>
            </div>
          )}
          <BriefingForm
            tipo={(briefing?.tipo as any) || 'SAAS_PRAGAS'}
            onSubmit={handleSubmit}
            onSave={handleSave}
            submitting={submitting}
            saving={saving}
            initialValues={briefing?.dados || undefined}
          />
        </div>
      </div>
    </div>
  );
}
